import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import './AddressLookupOrInputControl.sass';

import { FieldValidationResult } from '@payaca/types/fieldValidationTypes';

import AddressInputControl from '@payaca/components/addressInputControl/AddressInputControl';
import LabelValuePair from '@payaca/components/labelValuePair/LabelValuePair';

import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';

import { Button } from '@payaca/ambrosia';
import { InputStyleVariant } from '@payaca/components/inputWrapper/InputWrapper';
import { Address } from '@payaca/types/locationTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { useRegion } from '@/api/rest/users/useRegion';
import AddressLookupControl from '../addressLookupControl/AddressLookupControl';

type Props = {
  inputStyleVariant?: InputStyleVariant;
  address: Address;
  addressFieldName?: string;
  touchedState?: { [fieldName: string]: boolean };
  validationState?: { [fieldName: string]: FieldValidationResult };
  onChange: (value: { [key: string]: any }) => void;
  onTouch?: (fieldName: string) => void;
  onComponentRenderChange?: () => void;
  onSelectLookupAddress?: () => void;
};

const AddressLookupOrInputControl: FunctionComponent<Props> = ({
  inputStyleVariant,
  address,
  addressFieldName,
  touchedState,
  validationState,
  onTouch,
  onChange,
  onComponentRenderChange,
  onSelectLookupAddress,
}: Props): JSX.Element | null => {
  const region = useRegion();
  // const [isLoadingExistingAddress, setIsLoadingExistingAddress] =
  //   useState(true);
  const [isEnteringAddress, setIsEnteringAddress] = useState(false);
  const [isEnteringAddressManually, setIsEnteringAddressManually] =
    useState(false);

  useEffect(() => {
    if (onComponentRenderChange) {
      onComponentRenderChange();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'onComponentRenderChange'
  }, [isEnteringAddress, isEnteringAddressManually]);

  const addressAsString = useMemo(() => {
    if (!address) return;

    const addressElements = [];
    addressElements.push(address.line1);
    addressElements.push(address.line2);
    addressElements.push(address.city);
    addressElements.push(address.administrativeArea);
    addressElements.push(address.postcode);

    return addressElements.filter((x) => !!x).join(', ');
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: address.city
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: address.line1
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: address.line2
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has unnecessary dependency: address.postcode
  }, [
    address,
    address?.postcode,
    address?.city,
    address?.line1,
    address?.line2,
  ]);

  useEffect(() => {
    if (!addressAsString || addressAsString?.length === 0) {
      setIsEnteringAddress(true);
    }
  }, [addressAsString]);

  return (
    <ResponsiveViewWrapper
      downBreakpointSm={400}
      className="address-lookup-or-input-control flex space-x-3.5"
    >
      <React.Fragment>
        {!isEnteringAddressManually && !isEnteringAddress && (
          <>
            <div className="existing-address-container">
              <LabelValuePair label="Address" value={addressAsString} />
            </div>
            <div className="address-controls-container">
              <Button
                variant="link"
                onClick={() => {
                  setIsEnteringAddress(true);
                  setIsEnteringAddressManually(true);
                }}
              >
                <UntitledIcon name="edit-02.3" />
              </Button>
            </div>
          </>
        )}
        {isEnteringAddress && (
          <div className="address-input-container">
            {!isEnteringAddressManually && (
              <AddressLookupControl
                onChange={(value: { [key: string]: any }) => {
                  onChange(value);
                  setIsEnteringAddress(false);
                  onSelectLookupAddress?.();
                }}
                addressFieldName={addressFieldName}
                inputStyleVariant={inputStyleVariant}
                onEnterManually={() => setIsEnteringAddressManually(true)}
                label="Address"
              />
            )}
            {isEnteringAddressManually && (
              <>
                <AddressInputControl
                  styleVariant={inputStyleVariant}
                  address={address}
                  fieldNamePrefix={addressFieldName}
                  region={region}
                  onChange={onChange}
                  onTouch={onTouch}
                  validationState={validationState}
                  touchedState={touchedState}
                />
                <div className="lookup-address-trigger-container">
                  <Button
                    variant="link"
                    onClick={() => setIsEnteringAddressManually(false)}
                  >
                    Lookup address
                    <UntitledIcon name="chevron-right" />
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </React.Fragment>
    </ResponsiveViewWrapper>
  );
};

export default AddressLookupOrInputControl;
